import { useEffect } from "react";
import { create } from "zustand";
import type { CallState, UserSummary, VoiceParticipant } from "@atrium/shared";

/* ───────────── Who is in which voice or stage channel (pushed by the server) ───────────── */

interface VoiceStatesStore {
  byCommunity: Record<string, VoiceParticipant[] | undefined>;
  /** Live stage topics by channel. */
  stageTopics: Record<string, string | undefined>;
  setChannel: (communityId: string, channelId: string, participants: VoiceParticipant[], topic?: string) => void;
  seed: (communityId: string, participants: VoiceParticipant[]) => void;
  setTopic: (channelId: string, topic: string) => void;
}

export const useVoiceStatesStore = create<VoiceStatesStore>((set, get) => ({
  byCommunity: {},
  stageTopics: {},
  setChannel: (communityId, channelId, participants, topic) => {
    const cur = get().byCommunity[communityId] ?? [];
    set({
      byCommunity: { ...get().byCommunity, [communityId]: [...cur.filter((p) => p.channelId !== channelId), ...participants] },
      ...(topic !== undefined ? { stageTopics: { ...get().stageTopics, [channelId]: topic } } : {}),
    });
  },
  seed: (communityId, participants) => set({ byCommunity: { ...get().byCommunity, [communityId]: participants } }),
  setTopic: (channelId, topic) => set({ stageTopics: { ...get().stageTopics, [channelId]: topic } }),
}));

/** Structure snapshots already applied. Re-mounting with a cached snapshot mustn't overwrite newer live state. */
const seeded = new WeakSet<VoiceParticipant[]>();

export function useVoiceStates(communityId: string, initial: VoiceParticipant[]) {
  const live = useVoiceStatesStore((s) => s.byCommunity[communityId]);
  const seed = useVoiceStatesStore((s) => s.seed);
  useEffect(() => {
    if (seeded.has(initial) && useVoiceStatesStore.getState().byCommunity[communityId]) return;
    seeded.add(initial);
    seed(communityId, initial);
  }, [communityId, initial, seed]);
  return live ?? initial;
}

export const useStageTopic = (channelId: string) => useVoiceStatesStore((s) => s.stageTopics[channelId] ?? "");

/* ───────────── Calls in conversations ───────────── */

export interface IncomingCall {
  conversationId: string;
  from: UserSummary;
  call: CallState;
  /** When it started ringing here; rings stop on their own after a while. */
  at: number;
}

interface CallStore {
  byConversation: Record<string, CallState | undefined>;
  incoming: IncomingCall[];
  setCall: (conversationId: string, call: CallState | null) => void;
  reset: () => void;
  ring: (c: IncomingCall) => void;
  stopRing: (conversationId: string) => void;
}

export const useCallStore = create<CallStore>((set, get) => ({
  byConversation: {},
  incoming: [],
  setCall: (conversationId, call) => {
    const next = { ...get().byConversation };
    if (call) next[conversationId] = call;
    else delete next[conversationId];
    set({ byConversation: next, ...(call ? {} : { incoming: get().incoming.filter((i) => i.conversationId !== conversationId) }) });
  },
  reset: () => set({ byConversation: {}, incoming: [] }),
  ring: (c) => set({ incoming: [...get().incoming.filter((i) => i.conversationId !== c.conversationId), c] }),
  stopRing: (conversationId) => set({ incoming: get().incoming.filter((i) => i.conversationId !== conversationId) }),
}));

export const useCall = (conversationId: string | undefined) => useCallStore((s) => (conversationId ? s.byConversation[conversationId] : undefined));

/* ───────────── Screen share viewing (this device) ───────────── */

const VOLUME_KEY = "atrium:stream:volume";
type Volumes = Record<string, { volume: number; muted: boolean } | undefined>;
const loadVolumes = (): Volumes => {
  try {
    return JSON.parse(localStorage.getItem(VOLUME_KEY) ?? "{}") as Volumes;
  } catch {
    return {};
  }
};

export type GoLiveMode = "start" | "edit";

interface StreamUiStore {
  /** Stream audio per streamer (user id), remembered across sessions. */
  volumes: Volumes;
  setVolume: (userId: string, volume: number) => void;
  toggleMuted: (userId: string) => void;
  /** The stream (socket id) shown large in the room view and the mini player. */
  focused: string | null;
  setFocused: (socketId: string | null) => void;
  /** Room views currently on screen, by room id. The mini player shows when you leave yours. */
  views: Record<string, number>;
  addView: (roomId: string) => () => void;
  /** Your own stream's preview tile, hidden to avoid a hall of mirrors. */
  hideSelfPreview: boolean;
  setHideSelfPreview: (v: boolean) => void;
  goLive: GoLiveMode | null;
  openGoLive: (mode: GoLiveMode | null) => void;
}

export const useStreamUi = create<StreamUiStore>((set, get) => {
  const saveVolumes = (volumes: Volumes) => {
    set({ volumes });
    try {
      localStorage.setItem(VOLUME_KEY, JSON.stringify(volumes));
    } catch {
      /* storage full or blocked */
    }
  };
  return {
    volumes: loadVolumes(),
    setVolume: (userId, volume) => saveVolumes({ ...get().volumes, [userId]: { volume: Math.min(1, Math.max(0, volume)), muted: volume <= 0 } }),
    toggleMuted: (userId) => {
      const cur = get().volumes[userId] ?? { volume: 1, muted: false };
      saveVolumes({ ...get().volumes, [userId]: { volume: cur.volume > 0 ? cur.volume : 1, muted: !cur.muted } });
    },
    focused: null,
    setFocused: (focused) => set({ focused }),
    views: {},
    addView: (roomId) => {
      set({ views: { ...get().views, [roomId]: (get().views[roomId] ?? 0) + 1 } });
      return () => set({ views: { ...get().views, [roomId]: Math.max(0, (get().views[roomId] ?? 1) - 1) } });
    },
    hideSelfPreview: false,
    setHideSelfPreview: (hideSelfPreview) => set({ hideSelfPreview }),
    goLive: null,
    openGoLive: (goLive) => set({ goLive }),
  };
});

export const useStreamVolume = (userId: string) => useStreamUi((s) => s.volumes[userId]) ?? { volume: 1, muted: false };
